import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * Animated cartoon duck mascot.
 * Yellow duck wearing a DCC playing top, no pants, carrying a cricket bat.
 * Peeks from the left, looks at the viewer, walks across the top edge of
 * the sponsors section, and disappears off the right side.
 * Plays a quiet quack when peeking. Repeats every 60 seconds.
 */

// Synthesise a short duck quack via Web Audio API (no external files needed).
function playQuack(loud = false) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const vol = loud ? 0.3 : 0.12;
    const duration = loud ? 0.25 : 0.35;

    // Master gain
    const master = ctx.createGain();
    master.gain.setValueAtTime(vol, now);
    master.gain.linearRampToValueAtTime(0, now + duration);
    master.connect(ctx.destination);

    // Main "quack" oscillator — quick frequency drop mimics a duck
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(loud ? 880 : 680, now);
    osc.frequency.exponentialRampToValueAtTime(loud ? 300 : 220, now + duration * 0.35);
    osc.frequency.exponentialRampToValueAtTime(loud ? 240 : 180, now + duration * 0.8);

    // Band-pass filter to shape the nasal quack tone
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(loud ? 1000 : 800, now);
    filter.Q.setValueAtTime(loud ? 5 : 3, now);

    osc.connect(filter);
    filter.connect(master);
    osc.start(now);
    osc.stop(now + duration);

    // If loud (startled), play a second quack right after for a "QUACK-QUACK!"
    if (loud) {
      const osc2 = ctx.createOscillator();
      osc2.type = "sawtooth";
      const t2 = now + 0.2;
      osc2.frequency.setValueAtTime(780, t2);
      osc2.frequency.exponentialRampToValueAtTime(260, t2 + 0.18);

      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.22, t2);
      g2.gain.linearRampToValueAtTime(0, t2 + 0.22);
      g2.connect(ctx.destination);

      const f2 = ctx.createBiquadFilter();
      f2.type = "bandpass";
      f2.frequency.setValueAtTime(900, t2);
      f2.Q.setValueAtTime(4, t2);

      osc2.connect(f2);
      f2.connect(g2);
      osc2.start(t2);
      osc2.stop(t2 + 0.22);
    }

    // Cleanup
    osc.onended = () => { ctx.close().catch(() => {}); };
  } catch {
    // Silent fail — audio isn't critical
  }
}

export function DccDuck({ height = 150 }) {
  const [visible, setVisible] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [startled, setStartled] = useState(false);
  const startledTimeout = useRef(null);
  const hasInteracted = useRef(false);

  // Seasonal accessory: Santa hat (Dec), sun visor (Jan/Feb — Aus summer)
  const month = new Date().getMonth(); // 0-indexed
  const season = month === 11 ? "santa" : (month === 0 || month === 1) ? "visor" : "none";

  // Track any user interaction so we can use Web Audio (browsers block
  // audio until a gesture has occurred).
  useEffect(() => {
    const mark = () => { hasInteracted.current = true; };
    window.addEventListener("pointerdown", mark, { once: false, passive: true });
    window.addEventListener("keydown", mark, { once: false, passive: true });
    return () => {
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
    };
  }, []);

  const quack = useCallback((loud = false) => {
    if (hasInteracted.current) playQuack(loud);
  }, []);

  // --- Click handler: startled easter egg ---
  const handleDuckClick = useCallback((e) => {
    e.stopPropagation();
    if (startled) return; // no stacking
    hasInteracted.current = true;
    setStartled(true);

    // Burst of feathers from the duck's position
    const duckEl = duckRef.current;
    const containerEl = containerRef.current;
    if (duckEl && containerEl) {
      const duckRect = duckEl.getBoundingClientRect();
      const contRect = containerEl.getBoundingClientRect();
      const cx = duckRect.left - contRect.left + duckRect.width * 0.5;
      const cy = duckRect.top - contRect.top + duckRect.height * 0.4;

      const burst = [];
      for (let i = 0; i < 12; i++) {
        burst.push({
          id: featherIdRef.current++,
          x: cx + (Math.random() - 0.5) * 30,
          y: cy + (Math.random() - 0.5) * 20,
          drift: (Math.random() - 0.5) * 80,
          rot: Math.random() * 360,
          size: 7 + Math.random() * 8,
          dur: 1.0 + Math.random() * 1.0,
        });
      }
      setFeathers((prev) => [...prev.slice(-15), ...burst]);
    }

    // Clear startled after animation
    clearTimeout(startledTimeout.current);
    startledTimeout.current = setTimeout(() => setStartled(false), 1200);
  }, [startled, quack]);

  useEffect(() => {
    // Only appear once every 24 hours — store last appearance in localStorage.
    const STORAGE_KEY = "dcc_duck_last_seen";
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;

    try {
      const lastSeen = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (Date.now() - lastSeen < ONE_DAY_MS) return; // already appeared today
    } catch { /* private browsing — allow */ }

    // Random delay between 3s and 30s so the appearance feels surprising
    const delay = 3000 + Math.random() * 27000;
    const timer = setTimeout(() => {
      setVisible(true);
      setCycle((c) => c + 1);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  // Play a quiet quack when the duck first peeks in
  useEffect(() => {
    if (!visible) return;
    const quackTimer = setTimeout(() => quack(false), 600);
    return () => clearTimeout(quackTimer);
  }, [visible, cycle, quack]);

  // After animation completes (~18s), hide until next cycle
  useEffect(() => {
    if (!visible) return;
    const hideTimer = setTimeout(() => setVisible(false), 18000);
    return () => clearTimeout(hideTimer);
  }, [visible, cycle]);

  // --- Feather trail ---
  const duckRef = useRef(null);
  const containerRef = useRef(null);
  const [feathers, setFeathers] = useState([]);
  const featherIdRef = useRef(0);

  useEffect(() => {
    if (!visible) { setFeathers([]); return; }

    // Spawn a feather every ~400ms while walking (after the 2s peek phase)
    const spawnInterval = setInterval(() => {
      const duckEl = duckRef.current;
      const containerEl = containerRef.current;
      if (!duckEl || !containerEl) return;

      const duckRect = duckEl.getBoundingClientRect();
      const contRect = containerEl.getBoundingClientRect();

      // Position feather relative to the container, at the duck's tail area
      const x = duckRect.left - contRect.left + duckRect.width * 0.3;
      const y = duckRect.top - contRect.top + duckRect.height * 0.55;

      // Only spawn if duck is within viewport-ish bounds
      if (x < -20 || x > contRect.width + 20) return;

      const id = featherIdRef.current++;
      const drift = (Math.random() - 0.5) * 30;  // horizontal drift
      const rot = Math.random() * 360;
      const size = 6 + Math.random() * 6;
      const dur = 1.2 + Math.random() * 0.8;

      setFeathers((prev) => {
        // Cap at 30 feathers to avoid memory bloat
        const trimmed = prev.length > 29 ? prev.slice(-20) : prev;
        return [...trimmed, { id, x, y, drift, rot, size, dur }];
      });
    }, 400);

    return () => clearInterval(spawnInterval);
  }, [visible, cycle]);

  // Auto-remove feathers after they fade out
  useEffect(() => {
    if (feathers.length === 0) return;
    const cleanup = setTimeout(() => {
      setFeathers((prev) => prev.slice(1));
    }, 2200);
    return () => clearTimeout(cleanup);
  }, [feathers.length]);

  if (!visible) return null;

  const w = height * 0.7;

  return (
    <div
      className="duck-container"
      ref={containerRef}
      key={cycle}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: height + 60,
        pointerEvents: "none",
        overflow: "clip",
        overflowClipMargin: "0px",
        clipPath: `inset(-80px -5px 0px -5px)`,
        zIndex: 30,
        transform: `translateY(-${height - 4}px)`,
      }}
      data-testid="dcc-duck-container"
    >
      {/* Feather particles */}
      {feathers.map((f) => (
        <svg
          key={f.id}
          className="duck-feather"
          style={{
            position: "absolute",
            left: f.x,
            top: f.y,
            width: f.size,
            height: f.size * 2.2,
            animationDuration: `${f.dur}s`,
            "--feather-drift": `${f.drift}px`,
            "--feather-rot": `${f.rot}deg`,
          }}
          viewBox="0 0 10 22"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M 5 0 Q 9 5 8 11 Q 7 17 5 22 Q 3 17 2 11 Q 1 5 5 0 Z"
            fill="#FFD93D"
            opacity="0.85"
          />
          <line x1="5" y1="2" x2="5" y2="20" stroke="#F5C623" strokeWidth="0.5" />
        </svg>
      ))}

      <div
        className={`duck-animator ${startled ? "duck-startled" : ""}`}
        ref={duckRef}
        style={{ width: w, height: height, pointerEvents: "auto", cursor: "pointer" }}
        onClick={handleDuckClick}
        role="button"
        aria-label="Click the duck!"
        tabIndex={0}
        data-testid="dcc-duck-clickable"
      >
        {/* Speech bubble on startled */}
        {startled && (
          <div className="duck-speech-bubble" style={{
            position: "absolute",
            top: -8,
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
            zIndex: 5,
          }}>
            <span style={{
              display: "inline-block",
              background: "#FFFFFF",
              color: "#1E3A8A",
              fontFamily: "'Barlow Condensed', 'Inter', sans-serif",
              fontWeight: 900,
              fontSize: height * 0.18,
              letterSpacing: "1px",
              padding: "2px 8px",
              borderRadius: 6,
              border: "2px solid #1E3A8A",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            }}>
              QUACK!!
            </span>
            {/* Bubble tail */}
            <div style={{
              width: 0, height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "8px solid #1E3A8A",
              margin: "0 auto",
            }} />
          </div>
        )}
        <svg
          viewBox="0 0 180 260"
          width={w}
          height={height}
          xmlns="http://www.w3.org/2000/svg"
          data-testid="dcc-duck-svg"
        >
          {/* === WALKING LEGS (animated) === */}
          {/* Left leg */}
          <g className="duck-leg-left">
            <line x1="72" y1="195" x2="62" y2="238" stroke="#E8991C" strokeWidth="7" strokeLinecap="round" />
            {/* Foot */}
            <ellipse cx="56" cy="242" rx="14" ry="5" fill="#E8991C" />
          </g>
          {/* Right leg */}
          <g className="duck-leg-right">
            <line x1="98" y1="195" x2="108" y2="238" stroke="#E8991C" strokeWidth="7" strokeLinecap="round" />
            {/* Foot */}
            <ellipse cx="114" cy="242" rx="14" ry="5" fill="#E8991C" />
          </g>

          {/* === BODY (yellow oval) === */}
          <ellipse cx="85" cy="160" rx="50" ry="55" fill="#FFD93D" />

          {/* === DCC PLAYING TOP (royal blue cricket shirt) === */}
          <path
            d="M 42 130 Q 42 110 60 108 L 85 105 L 110 108 Q 128 110 128 130 L 130 170 Q 128 178 110 180 L 60 180 Q 42 178 40 170 Z"
            fill="#1E3A8A"
          />
          {/* Shirt collar */}
          <path
            d="M 68 105 Q 85 100 102 105"
            stroke="#2749C9"
            strokeWidth="3"
            fill="none"
          />
          {/* DCC text on shirt */}
          <text
            x="85"
            y="148"
            textAnchor="middle"
            fill="#FFFFFF"
            fontSize="18"
            fontWeight="900"
            fontFamily="'Barlow Condensed', 'Inter', sans-serif"
            letterSpacing="2"
          >
            DCC
          </text>
          {/* Shirt sleeve trim (left) */}
          <path d="M 42 125 Q 30 130 28 140 Q 30 145 42 142" fill="#1E3A8A" />
          <line x1="28" y1="140" x2="42" y2="142" stroke="#4169E1" strokeWidth="1.5" />
          {/* Shirt sleeve trim (right) */}
          <path d="M 128 125 Q 140 130 142 140 Q 140 145 128 142" fill="#1E3A8A" />
          <line x1="142" y1="140" x2="128" y2="142" stroke="#4169E1" strokeWidth="1.5" />

          {/* === WING (left - holding bat) === */}
          <g className="duck-wing-bat">
            {/* Cricket bat */}
            <rect x="10" y="90" width="10" height="80" rx="3" fill="#C4956A" transform="rotate(-15 15 130)" />
            {/* Bat blade */}
            <rect x="8" y="60" width="14" height="36" rx="2" fill="#E8C99A" transform="rotate(-15 15 78)" />
            {/* Bat handle grip */}
            <rect x="12" y="88" width="6" height="14" rx="1" fill="#2A2A2A" transform="rotate(-15 15 95)" />
            <line x1="13" y1="92" x2="17" y2="91" stroke="#555" strokeWidth="1" transform="rotate(-15 15 91)" />
            <line x1="13" y1="96" x2="17" y2="95" stroke="#555" strokeWidth="1" transform="rotate(-15 15 95)" />
            {/* Wing */}
            <ellipse cx="38" cy="148" rx="18" ry="25" fill="#FFD93D" transform="rotate(10 38 148)" />
            <ellipse cx="36" cy="148" rx="14" ry="20" fill="#F5C623" transform="rotate(10 36 148)" />
          </g>

          {/* === WING (right) === */}
          <ellipse cx="128" cy="148" rx="15" ry="22" fill="#FFD93D" transform="rotate(-5 128 148)" />
          <ellipse cx="130" cy="148" rx="11" ry="17" fill="#F5C623" transform="rotate(-5 130 148)" />

          {/* === NECK === */}
          <ellipse cx="85" cy="100" rx="22" ry="15" fill="#FFD93D" />

          {/* === HEAD === */}
          <circle cx="85" cy="68" r="32" fill="#FFD93D" />

          {/* Head tilt for looking at viewer */}
          <g className="duck-head-look">
            {/* Eye (left) */}
            <circle cx="72" cy="58" r="6" fill="#FFFFFF" />
            <circle cx="73" cy="57" r="3.5" fill="#1A1A1A" />
            <circle cx="74.5" cy="55.5" r="1.2" fill="#FFFFFF" />

            {/* Eye (right) */}
            <circle cx="97" cy="58" r="5.5" fill="#FFFFFF" />
            <circle cx="96" cy="57" r="3" fill="#1A1A1A" />
            <circle cx="97" cy="55.5" r="1" fill="#FFFFFF" />

            {/* Beak */}
            <ellipse cx="85" cy="75" rx="16" ry="8" fill="#E8991C" />
            <ellipse cx="85" cy="73" rx="15" ry="6" fill="#F5A623" />
            {/* Beak line */}
            <line x1="70" y1="74" x2="100" y2="74" stroke="#D4851C" strokeWidth="1.2" />
            {/* Little smile */}
            <path d="M 78 78 Q 85 82 92 78" stroke="#D4851C" strokeWidth="1.2" fill="none" />
          </g>

          {/* === HAIR / TUFT on top === */}
          {season === "none" && (
            <>
              <path d="M 82 37 Q 80 28 85 25 Q 90 28 88 37" fill="#F5C623" />
              <path d="M 78 40 Q 74 32 78 28" stroke="#F5C623" strokeWidth="2" fill="none" />
            </>
          )}

          {/* === SEASONAL: SANTA HAT (December) === */}
          {season === "santa" && (
            <g data-testid="duck-santa-hat">
              {/* White fur trim band across forehead */}
              <ellipse cx="85" cy="42" rx="36" ry="8" fill="#FFFFFF" />
              <ellipse cx="85" cy="42" rx="34" ry="6" fill="#F0F0F0" />
              {/* Red hat body */}
              <path d="M 52 42 Q 55 10 85 -2 Q 115 -8 130 5 Q 118 15 110 42 Z" fill="#CC2936" />
              {/* Darker red fold */}
              <path d="M 60 38 Q 70 18 95 5 Q 110 0 120 12" stroke="#A3202A" strokeWidth="1.5" fill="none" />
              {/* Droopy tip curving to the right */}
              <path d="M 125 8 Q 140 -5 148 5 Q 150 15 142 18" fill="#CC2936" stroke="#A3202A" strokeWidth="0.8" />
              {/* White pom-pom at tip */}
              <circle cx="145" cy="12" r="9" fill="#FFFFFF" />
              <circle cx="143" cy="10" r="4" fill="#F5F5F5" opacity="0.6" />
              {/* Fur trim texture dots */}
              <circle cx="60" cy="42" r="2" fill="#F5F5F5" opacity="0.5" />
              <circle cx="72" cy="40" r="1.5" fill="#F5F5F5" opacity="0.5" />
              <circle cx="98" cy="40" r="1.5" fill="#F5F5F5" opacity="0.5" />
              <circle cx="110" cy="42" r="2" fill="#F5F5F5" opacity="0.5" />
            </g>
          )}

          {/* === SEASONAL: SUN VISOR (Jan/Feb — Aussie summer) === */}
          {season === "visor" && (
            <g data-testid="duck-sun-visor">
              {/* Visor headband */}
              <path d="M 54 48 Q 60 38 85 36 Q 110 38 116 48" stroke="#1E3A8A" strokeWidth="5" fill="none" strokeLinecap="round" />
              {/* Visor brim — wide curved brim sticking forward */}
              <path d="M 50 48 Q 45 42 30 44 Q 22 46 20 52 Q 22 56 35 54 Q 48 53 54 50 Z" fill="#1E3A8A" />
              {/* Brim highlight */}
              <path d="M 35 47 Q 42 45 50 47" stroke="#2749C9" strokeWidth="1" fill="none" />
              {/* DCC text on visor band */}
              <text
                x="85"
                y="47"
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize="7"
                fontWeight="900"
                fontFamily="'Barlow Condensed', 'Inter', sans-serif"
                letterSpacing="1"
              >
                DCC
              </text>
              {/* Hair tuft poking out the top of visor */}
              <path d="M 82 37 Q 80 28 85 25 Q 90 28 88 37" fill="#F5C623" />
            </g>
          )}

          {/* === TAIL FEATHERS === */}
          <path d="M 125 155 Q 140 140 145 150 Q 142 160 130 162" fill="#F5C623" />
          <path d="M 128 150 Q 148 132 150 145 Q 146 155 133 158" fill="#FFD93D" />
        </svg>
      </div>

      <style>{`
        .duck-animator {
          position: absolute;
          bottom: 0;
          animation: duckWalkCycle 18s linear forwards;
        }

        @keyframes duckWalkCycle {
          /* Start fully off-screen left */
          0% {
            left: -12%;
            opacity: 0;
          }
          /* Peek head in */
          3% {
            left: -5%;
            opacity: 1;
          }
          /* Pause and look at viewer */
          8% {
            left: -2%;
            opacity: 1;
          }
          12% {
            left: 0%;
            opacity: 1;
          }
          /* Walk across */
          85% {
            left: 100%;
            opacity: 1;
          }
          /* Exit off right */
          95% {
            left: 108%;
            opacity: 1;
          }
          100% {
            left: 112%;
            opacity: 0;
          }
        }

        /* Waddle body bounce */
        .duck-animator {
          animation: duckWalkCycle 18s linear forwards, duckWaddle 0.5s ease-in-out infinite;
          animation-delay: 0s, 2s;
        }

        @keyframes duckWaddle {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50% { transform: rotate(2deg) translateY(-3px); }
        }

        /* Leg walk cycle */
        .duck-leg-left {
          animation: legSwingLeft 0.5s ease-in-out infinite;
          transform-origin: 72px 195px;
          animation-delay: 2s;
        }
        .duck-leg-right {
          animation: legSwingRight 0.5s ease-in-out infinite;
          transform-origin: 98px 195px;
          animation-delay: 2s;
        }

        @keyframes legSwingLeft {
          0%, 100% { transform: rotate(-15deg); }
          50% { transform: rotate(15deg); }
        }
        @keyframes legSwingRight {
          0%, 100% { transform: rotate(15deg); }
          50% { transform: rotate(-15deg); }
        }

        /* Head look at viewer during peek */
        .duck-head-look {
          animation: headLook 18s linear forwards;
        }
        @keyframes headLook {
          0%, 3% { transform: rotate(0deg); }
          5% { transform: translateX(3px) rotate(5deg); }
          10% { transform: translateX(3px) rotate(5deg); }
          14% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }

        /* Wing/bat bob */
        .duck-wing-bat {
          animation: wingBob 0.5s ease-in-out infinite;
          transform-origin: 38px 148px;
          animation-delay: 2s;
        }
        @keyframes wingBob {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }

        /* Feather particle trail */
        .duck-feather {
          pointer-events: none;
          animation: featherFall var(--dur, 1.5s) ease-out forwards;
          transform-origin: center;
        }
        @keyframes featherFall {
          0% {
            opacity: 0.8;
            transform: translateX(0) translateY(0) rotate(var(--feather-rot, 0deg)) scale(1);
          }
          30% {
            opacity: 0.7;
          }
          100% {
            opacity: 0;
            transform: translateX(var(--feather-drift, 10px)) translateY(30px) rotate(calc(var(--feather-rot, 0deg) + 180deg)) scale(0.4);
          }
        }

        /* Startled easter egg — jump + spin */
        .duck-startled {
          animation:
            duckWalkCycle 18s linear forwards,
            duckStartledJump 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards !important;
        }
        @keyframes duckStartledJump {
          0%   { transform: translateY(0) rotate(0deg) scale(1); }
          15%  { transform: translateY(-40px) rotate(-15deg) scale(1.15); }
          30%  { transform: translateY(-55px) rotate(10deg) scale(1.2); }
          50%  { transform: translateY(-45px) rotate(360deg) scale(1.1); }
          70%  { transform: translateY(-15px) rotate(370deg) scale(1.05); }
          85%  { transform: translateY(4px) rotate(360deg) scale(0.97); }
          100% { transform: translateY(0) rotate(360deg) scale(1); }
        }

        /* Speech bubble pop-in */
        .duck-speech-bubble {
          animation: speechPop 1.2s ease-out forwards;
        }
        @keyframes speechPop {
          0%   { opacity: 0; transform: translateX(-50%) scale(0.3) translateY(8px); }
          15%  { opacity: 1; transform: translateX(-50%) scale(1.15) translateY(-4px); }
          25%  { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
          75%  { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) scale(0.8) translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
